import React, {Component} from 'react';
import SearchResultsMarker from './SearchResultsMarker';
import PropTypes from 'prop-types';
import _debounce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import {connect} from 'react-redux';
import {Popover} from 'reactstrap';
import {withRouter} from 'react-router-dom';
import OdinCheckbox from '../Forms/OdinCheckbox';
import styles from './SearchResultsMap.scss';
import {createRegionPointsSearchQuery, createSearchQuery} from '../../lib/createSearchQuery';
import {dataLayerPush, getPageData, nameFormat} from '../../lib/tracking';
import {resetSearchPropertyMarker, fetchMapProperties} from '../../actions';
import GoogleMap from '../GoogleMap/GoogleMap';
import PropertyCardSearch from '../PropertyCard/PropertyCardSearch';
import {createBasicParams} from '../../pages/PropertySearch';

const legend = [
  {
    'icon': 'marker-offmarket',
    'desc': 'Off-market',
  },
  {
    'icon': 'marker-sold',
    'desc': 'Sold',
  },
  {
    'icon': 'marker-for-sale',
    'desc': 'For sale',
  },
  {
    'icon': 'marker-for-rent',
    'desc': 'For rent',
  },
  {
    'icon': 'marker-multiple-properties',
    'desc': 'Multiples at address',
  }
];

// default lat/long when there are no results
const defaultCenter = {
  lat: -25.053730,
  lng: 134.204175
};
const defaultZoom = 4;
const numberOfPropertiesFetched = 100;

class SearchResultsMap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      markerLabel: false,
      enableUpdateForMapMove: true,
      currentProperty: null,
      popupVisible: false,
      popTarget: null,
      popContent: '',
    };
    this.map = null;
    this.maps = null;
    this.fitBoundsZoom = true;
    this.debouncedFetch = _debounce(this.refetchForMapMove, 250);

    this.oldZoom = defaultZoom;
    this.newZoom = defaultZoom;
  }

  componentDidMount() {
    this.fetchMapProperties();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location.key !== this.props.location.key) {
      this.fetchMapProperties();
    }
    if (this.props.searchSource === 'search' && !_isEmpty(this.props.list) && !_isEqual(prevProps.list, this.props.list)) {
      this.fitToBounds(this.props.list);
    }
    if (this.map && this.props.list.length === 0) {
      this.map.setCenter({
        lat: -25.053730,
        lng: 134.204175
      })
      this.fitBoundsZoom = true;
      this.map.setZoom(4);
    }
  }
  fetchMapProperties = () => {
    const basicParams = createBasicParams(this.props);
    const queries = createSearchQuery(basicParams, numberOfPropertiesFetched);
    const payload = {source: 'search', queries: queries, newsearch: true};
    this.props.fetchMapProperties(payload);
  }
  createMapOptions = () => ({mapTypeControl: false, fullscreenControl: false})
  cancelCard = () => {
    if (this.state.popupVisible) this.setState({popupVisible: false});
  }
  markerClick = (index, child) => {
    const currentCards = child.properties.map((item, index) => <PropertyCardSearch key={index} property={item} mapCard />);
    this.setState({currentProperty: child, popupVisible: true, popTarget: 'marker_' + child.index, popContent: currentCards});
    this.trackUserInteraction('marker click');
  }
  trackUserInteraction = (action) => {
    let pd = getPageData();
    if (pd && pd.name != 'unknown') {
      // not tracking ssr searches as it's not ui actioned
      let map_clicks = {
        event: 'map_clicks',
        map_interactions: action,
        suburb_name: nameFormat(this.props.filterSearch.address.suburb),
        postcode: this.props.filterSearch.address.postCode,
        state: this.props.filterSearch.address.stateCode.toUpperCase(),
        page_type: pd.type,
        page_name: pd.name,
      };
      dataLayerPush(map_clicks);
    }
  }

  render() {
    let boundary = 'scrollParent';
    if (typeof document != 'undefined') boundary = document.getElementById('mapContainer'); // it only uses this for dimensions
    return (
      <div className={'d-flex flex-column h-100 ' + styles.wrapper} id="mapContainer">
        <GoogleMap
          zoom={defaultZoom}
          center={defaultCenter}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({map, maps}) => this.initMap(map, maps)}
          onChildClick={this.markerClick}
          onDrag={this.cancelCard}
          onDragEnd={() => {
            this.refetchForMapMove();
            this.trackUserInteraction('map move');
          }}
          options={this.createMapOptions}
          onZoomAnimationEnd={() => {
            this.handleZoomEvent();
            if (!this.fitBoundsZoom) {
              this.debouncedFetch();
              this.newZoom = this.map.getZoom();
              if (this.newZoom > this.oldZoom) this.trackUserInteraction('map zoom in');
              else if (this.newZoom < this.oldZoom) this.trackUserInteraction('map zoom out');
              this.oldZoom = this.newZoom;
            }
            this.fitBoundsZoom = false;
          }}>
          {this.props.list !== [] ?
            this.props.list.map((item, index) =>
              <SearchResultsMarker
                index={index}
                key={index}
                lat={item.lat}
                lng={item.lon}
                category={item.category}
                text={item.streetAddress}
                properties={item.properties}
                label={this.state.markerLabel}
              />
            ): <span>There is no property return for this search.</span>
          }
        </GoogleMap>
        <div className={'position-absolute rounded-sm p-2 d-flex ' + styles.syncCheckbox}>
          <OdinCheckbox
            id="enableMapMove"
            checked={this.state.enableUpdateForMapMove}
            onChange={() => {this.setState({enableUpdateForMapMove: !this.state.enableUpdateForMapMove})}}
          />
          <div className="d-block smText"><label className="mt-1 mb-1" htmlFor="enableMapMove">Update when I move the map.</label></div>
        </div>
        <div className={'flex-row flex-wrap d-none d-sm-flex p-2 position-absolute ' + styles.legend}>
          {legend.map((item, index) =>
            <div className={styles.legendItem} key={index}>
              <img className={'mr-2 ' + styles.legendIcon} src={`/assets/images/map/${item.icon}.png`} />
              <span className="smText mr-2">{item.desc}</span>
            </div>
          )}
        </div>
        {this.state.popTarget &&
          <Popover
            fade={false}
            trigger="legacy"
            className={(this.state.popContent.length > 1) ? styles.popOverLarge : styles.popOverSmall}
            boundariesElement={boundary}
            placement="auto"
            isOpen={this.state.popupVisible}
            target={this.state.popTarget}
            toggle={() => this.setState({popupVisible: !this.state.popupVisible})}
          >
            {this.state.popContent}
          </Popover>}
      </div>
    );
  }

  componentWillUnmount() {
    // reset the map instance when component unmounted.
    this.maps = null;
    this.map = null;
    this.props.resetSearchPropertyMarker();
  }

  initMap = (map, maps) => {
    // called from google maps once it's loaded.
    this.map = map;
    this.maps = maps;
    this.fitToBounds(this.props.list);
  }

  fitToBounds = (filteredData) => {
    if (!_isEmpty(filteredData)) {
      // fit markers on screen
      if (this.maps) {
        const bounds = new this.maps.LatLngBounds();
        filteredData.forEach((item) => {
          let latLon = new this.maps.LatLng(item.lat, item.lon);
          bounds.extend(latLon);
        });
        this.map.fitBounds(bounds);
        this.fitBoundsZoom = true;
      }
    }
  }

  recenter = (filteredData) => {
    if (!_isEmpty(filteredData) && this.maps && this.map) {
      // fit markers on screen
      const bounds = new this.maps.LatLngBounds();
      filteredData.forEach((item) => {
        let latLon = new this.maps.LatLng(item.lat, item.lon);
        bounds.extend(latLon);
      });
      this.map.setCenter(bounds.getCenter());
    }
  }

  handleZoomEvent = () => {
    if (this.map.getZoom() >= 17 && !this.state.markerLabel) {
      this.setState({'markerLabel': true});
    }
    else if (this.map.getZoom() < 17 && this.state.markerLabel) {
      this.setState({'markerLabel': false});
    }
  }

  refetchForMapMove = () => {
    if (this.state.enableUpdateForMapMove) {
      let bounds = this.map.getBounds();
      let ne = bounds.getNorthEast();
      let sw = bounds.getSouthWest();
      let regionPoints = [];
      if ((ne.lat != null) && (sw.lng != null)) {
        // se, ne, nw, sw
        regionPoints = [
          {
            lat: sw.lat(),
            lon: ne.lng()
          }, {
            lat: ne.lat(),
            lon: ne.lng()
          }, {
            lat: ne.lat(),
            lon: sw.lng()
          }, {
            lat: sw.lat(),
            lon: sw.lng()
          }
        ];
      }
      let queries = createRegionPointsSearchQuery(this.props.filterSearch, regionPoints);
      let payload = {source: 'map', queries: queries};
      this.props.fetchMapProperties(payload);
    }
  }
}

SearchResultsMap.propTypes = {
  location: PropTypes.object,
  list: PropTypes.array.isRequired,
  markerLatLon: PropTypes.object,
  markerShow: PropTypes.bool,
  markerProperties: PropTypes.array,
  searchSource: PropTypes.string,
  filterSearch: PropTypes.object,
  fetchMapProperties: PropTypes.func,
  resetSearchPropertyMarker: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    searchSource: state.searchPropertiesMap.results.payload.source,
    list: state.searchPropertiesMap.results.content,
    markerLatLon: state.searchPropertiesMarker.latLon,
    markerShow: state.searchPropertiesMarker.show,
    markerProperties: state.searchPropertiesMarker.properties,
    filterSearch: state.filterSearch,
  };
}

const mapDispatchToProps = {resetSearchPropertyMarker, fetchMapProperties};
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SearchResultsMap));
